import React from 'react';
import styled from 'styled-components/macro';
import { ItemSettings } from 'components/Settings/ItemSettings';
import { Card } from 'antd';
import { Tooltip } from '@cognite/cogs.js';

export const CardItem = ({
  itemType,
  itemName,
  title,
  contentList,
  editAvailable = false,
  deleteAvailable = false,
  onOpen,
  onDelete,
  onRename,
}: {
  itemType: string;
  itemName: string;
  title: string;
  contentList?: Content[];
  editAvailable?: boolean;
  deleteAvailable?: boolean;
  onOpen: VoidFunction;
  onDelete: VoidFunction;
  onRename: (newName: string) => void;
}) => (
  <StyledCard onClick={onOpen}>
    <StyledTitleContainer>
      <StyledTitleTooltip
        disabled={title.length < 15}
        content={<div>{title}</div>}
      >
        <StyledTitle>
          <span>{title}</span>
        </StyledTitle>
      </StyledTitleTooltip>
      <StyledSettings>
        <ItemSettings
          editAvailable={editAvailable}
          deleteAvailable={deleteAvailable}
          itemType={itemType}
          itemName={itemName}
          onDelete={onDelete}
          onRename={(newName: string) => onRename(newName)}
          actionType="Delete"
        />
      </StyledSettings>
    </StyledTitleContainer>
    <StyledContent>
      {contentList?.map((content) => (
        <div key={content.key}>
          {content.heading && (
            <ContentHeading>{content.heading}</ContentHeading>
          )}
          {content.details && (
            <ContentDetails>{content.details}</ContentDetails>
          )}
        </div>
      ))}
    </StyledContent>
  </StyledCard>
);

export interface Content {
  heading?: React.ReactNode;
  details?: React.ReactNode;
  key: string;
}

export interface Tag {
  value: string;
  color: 'success' | 'primary';
}

const StyledTitle = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
  margin: 0 0.4rem;
  width: calc(100% - 18px);
  flex: 1;
  display: inline-flex;
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
  }
`;

const StyledSettings = styled.div`
  flex: 0;
`;

const StyledContent = styled.div`
  text-overflow: ellipsis;
  margin: 0.2rem 0.4rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledTitleTooltip = styled(Tooltip)`
  width: 200px;
  white-space: normal;
  word-break: break-word;
`;

const StyledTitleContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 18px;
  flex: 1;
  align-items: center;
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  > span {
    width: 100%;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const StyledCard = styled(Card)`
  box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.1);
  transition: all 250ms linear;
  cursor: pointer;
  position: relative;
  top: 0;
  border-radius: 5px;

  .ant-card-body {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 1.3rem 1rem 1.3rem 1rem;
  }

  &:hover {
    background-color: #fafafa;
  }
`;

const ContentHeading = styled.div`
  font-weight: 600;
  font-size: 12px;
`;

const ContentDetails = styled.div`
  font-size: 10px;
`;
